import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import ContentCard from "@/components/feature/ContentCard";
import { formatDate, getContentById, getRelatedItems, typeLabel, typePath } from "@/lib/content";
import { siteInfo } from "@/mocks/content";

const metaTitlePrefix = {
  drama: "剧情详情",
  actor: "演员资料",
  news: "影视资讯",
} as const;

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getContentById(id);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setShareOpen(false);
    setToast("");
  }, [id]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <PageShell>
        <PageMeta
          title="内容暂无或已被移除 - 嫂子的诱惑"
          description="该内容可能已被移除或链接有误，请返回首页浏览嫂子的诱惑剧情介绍、演员资料与影视资讯。"
        />
        <div className="mx-auto w-full max-w-[1280px] px-4 py-24 md:px-6 lg:px-10">
          <EmptyState
            icon="ri-file-warning-line"
            title="内容暂无或已被移除"
            description="你访问的页面可能已经调整，可以回到首页或从分类页继续浏览站内内容。"
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to={`/category/${encodeURIComponent("剧情介绍")}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              >
                浏览剧情介绍
              </Link>
            </div>
          </EmptyState>
        </div>
      </PageShell>
    );
  }

  const related = getRelatedItems(item, 4);
  const categoryPath = typePath[item.type];
  const categoryName = typeLabel[item.type];

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，可直接粘贴分享");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  const handleShareChannel = (channel: string) => {
    setToast(`${channel}分享为演示功能，暂未接入真实接口`);
  };

  const pageTitle = `${metaTitlePrefix[item.type]} - ${item.title} | 嫂子的诱惑`;
  const pageDescription = item.excerpt.slice(0, 110);

  return (
    <PageShell>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        keywords={`嫂子的诱惑,${categoryName},${item.title},${item.tags.join(",")}`}
      />

      <div className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6 lg:px-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: categoryName, to: categoryPath },
              { label: item.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1280px] gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[1fr_320px] lg:px-10">
        <article className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={categoryPath}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
            >
              <i className="ri-folder-line text-sm" />
              {categoryName}
            </Link>
            {item.type === "actor" && (
              <span className="rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-800">
                {item.roleType}
              </span>
            )}
            {item.type === "drama" && (
              <span className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900">
                {item.chapter} · {item.duration}
              </span>
            )}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[34px]">
            {item.title}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 md:text-base">{item.subtitle}</p>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm" />
              {formatDate(item.date)}
            </span>
            {item.type === "drama" && (
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-sm" />
                {item.views}次阅读
              </span>
            )}
            {item.type === "news" && (
              <span className="flex items-center gap-1.5">
                <i className="ri-newspaper-line text-sm" />
                来源：{item.source}
              </span>
            )}
            {item.type === "actor" && (
              <span className="flex items-center gap-1.5">
                <i className="ri-cake-2-line text-sm" />
                {item.birthday}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line text-sm" />
              {siteInfo.author.name}
            </span>
          </div>

          {item.type === "actor" ? (
            <div className="mt-7 grid gap-5 sm:grid-cols-[240px_1fr]">
              <div className="aspect-[4/5] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title} 演员资料照片`}
                  title={`${item.title} 饰 ${item.role} · 嫂子的诱惑演员资料`}
                  className="h-full w-full object-top"
                />
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-100/70 p-5">
                <p className="text-[11px] uppercase tracking-[0.22em] text-accent-700">饰演角色</p>
                <p className="mt-2 font-heading text-xl font-bold text-foreground-950">{item.role}</p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-7 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 剧照`}
                title={`${item.title} · 嫂子的诱惑剧照`}
                className="h-full w-full object-top"
              />
            </div>
          )}

          <div className="mt-8 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
            {item.body.map((section, index) => (
              <section key={section.heading} className={index === 0 ? "" : "mt-7"}>
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  {section.heading}
                </h2>
                <div className="prose-article mt-3 text-sm md:text-[15px]">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 18)}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {item.type === "actor" && item.stills.length > 0 && (
            <section className="mt-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">剧照</h2>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {item.stills.map((still, index) => (
                  <div
                    key={still}
                    className="aspect-[16/10] overflow-hidden rounded-lg bg-background-200"
                  >
                    <img
                      src={still}
                      alt={`${item.title} 剧照 ${index + 1}`}
                      title={`${item.title} 剧照 · 嫂子的诱惑`}
                      className="h-full w-full object-top"
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 rounded-lg border border-background-200/80 bg-background-100/70 p-4 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to={categoryPath}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line text-base" />
              返回{categoryName}
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-line text-base" />
                分享此页
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-link text-base" />
                复制链接
              </button>
            </div>
          </div>

          <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
            <i className="ri-error-warning-line mt-0.5 text-sm" />
            本站仅提供文字内容介绍与资料整理，暂无播放接口演示，正式观看请前往官方播出平台。
          </p>
        </article>

        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-magic-line text-base text-accent-700" />
              相关推荐
            </h2>
            <div className="mt-4 space-y-3">
              {related.map((entry) => (
                <Link
                  key={entry.id}
                  to={`/detail/${entry.id}`}
                  className="group flex gap-3"
                >
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={entry.cover}
                      alt={`${entry.title} 封面`}
                      title={`${entry.title} · 相关推荐`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-xs font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                      {entry.title}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-400">
                      {typeLabel[entry.type]} · {formatDate(entry.date)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-primary-900 p-5 text-background-100">
            <p className="text-[10px] uppercase tracking-[0.24em] text-accent-300">分类入口</p>
            <p className="mt-2 font-heading text-sm font-semibold text-background-50">
              继续浏览{categoryName}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-background-100/65">
              该分类按章节与标签整理内容，支持筛选、排序与分页浏览。
            </p>
            <Link
              to={categoryPath}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-300 hover:text-accent-200"
            >
              进入分类页
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-100/80 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">查看更多</h2>
            <div className="mt-3 space-y-2 text-sm">
              <Link
                to={`/category/${encodeURIComponent("剧情介绍")}`}
                className="flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-foreground-700 transition-colors hover:text-primary-700"
              >
                <span>剧情介绍</span>
                <i className="ri-arrow-right-s-line text-base text-foreground-400" />
              </Link>
              <Link
                to={`/category/${encodeURIComponent("演员资料")}`}
                className="flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-foreground-700 transition-colors hover:text-primary-700"
              >
                <span>演员资料</span>
                <i className="ri-arrow-right-s-line text-base text-foreground-400" />
              </Link>
              <Link
                to={`/category/${encodeURIComponent("影视资讯")}`}
                className="flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-foreground-700 transition-colors hover:text-primary-700"
              >
                <span>影视资讯</span>
                <i className="ri-arrow-right-s-line text-base text-foreground-400" />
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <section className="border-t border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 lg:px-10">
          <SectionHeading
            kicker="More to read"
            title={`同类型${categoryName}推荐`}
            actionLabel={`查看全部${categoryName}`}
            actionTo={categoryPath}
          />
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((entry) => (
              <ContentCard key={`grid-${entry.id}`} item={entry} />
            ))}
          </div>
        </div>
      </section>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-primary-950/50 px-4 animate-fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="分享此页"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 animate-rise-in"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-base font-semibold text-foreground-950">分享此页</p>
                <p className="mt-1 text-xs text-foreground-500">{item.title}</p>
              </div>
              <button
                type="button"
                aria-label="关闭分享弹窗"
                onClick={() => setShareOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-800"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="mt-5 grid gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-3 rounded-md border border-background-200 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-link text-base" />
                复制页面链接
              </button>
              <button
                type="button"
                onClick={() => handleShareChannel("微信")}
                className="flex items-center gap-3 rounded-md border border-background-200 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-wechat-line text-base" />
                分享到微信
              </button>
              <button
                type="button"
                onClick={() => handleShareChannel("微博")}
                className="flex items-center gap-3 rounded-md border border-background-200 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-weibo-line text-base" />
                分享到微博
              </button>
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
              社交平台分享为前端演示，不会真实发布内容。
            </p>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-rise-in rounded-full bg-primary-950 px-5 py-3 text-xs text-background-50">
          <i className="ri-checkbox-circle-line mr-1.5 text-sm text-accent-300" />
          {toast}
        </div>
      )}
    </PageShell>
  );
}